<template>
  <div class="audience-editor">
    <mode-cards
      :value="draft.mode"
      :options="modeOptions"
      name="audience-mode"
      legend="Who can receive"
      @input="setMode"
    />
    <sme-alert v-if="!fieldsWithValues.length" level="info">
      No HR fields are set up yet, so this award can only go to everyone in the company.
    </sme-alert>

    <div v-if="draft.mode === 'filters'" class="audience-editor__filters">
      <template v-for="(row, i) in draft.rows">
        <div v-if="i > 0" :key="`and-${i}`" class="audience-editor__and" aria-hidden="true">and</div>
        <audience-filter-row
          :key="`${row.key}-${i}`"
          :row="row"
          :field="fieldFor(row.key)"
          :available-fields="unusedFields"
          :stale-values="staleValuesFor(row.key)"
          :open-on-mount="row.key === openRowKey"
          @update="updateRow(i, $event)"
          @remove="removeRow(i)"
        />
      </template>

      <b-dropdown
        v-if="unusedFields.length"
        size="sm"
        variant="outline-secondary"
        class="audience-editor__add"
        data-test="add-filter"
        no-caret
      >
        <template #button-content><font-awesome-icon :icon="['fal', 'plus']" class="mr-1" /> Add filter</template>
        <b-dropdown-item
          v-for="f in unusedFields"
          :key="f.key"
          :data-test="`add-filter-${f.key}`"
          @click="addFilter(f.key)"
        >
          {{ f.label }}
        </b-dropdown-item>
      </b-dropdown>

      <p v-if="!hasValues" class="audience-editor__hint">Pick at least one value</p>
      <sme-alert v-else-if="isZero" level="warning" role="status">
        No one matches these filters yet — try removing a filter or choosing fewer fields.
      </sme-alert>
      <sme-alert v-else-if="!loading && count === null" level="warning" role="status">
        Couldn’t count employees right now. You can still save; the count will show next time.
      </sme-alert>
    </div>

    <p
      v-if="countText && !(draft.mode === 'filters' && !hasValues)"
      class="audience-editor__count"
      data-test="audience-count"
    >
      {{ countText }}
    </p>
  </div>
</template>

<script>
import { computed, nextTick, ref, toRef, watch } from 'vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import ModeCards from '@/pages/recognition/components/ModeCards.vue';
import AudienceFilterRow from '@/pages/recognition/components/audience/AudienceFilterRow.vue';
import useAudienceCount from '@/pages/recognition/composables/useAudienceCount';
import {
  audienceCountText,
  draftFromScope,
  fieldLabel,
  scopeFromDraft,
  staleEntries,
} from '@/pages/recognition/utils/audience';

const sameScope = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Edits apply as they are made: there is no confirm step. Until "Only some employees" has a
// value, the emitted scope stays unchosen (division: null) so the form won't save everyone.
export default {
  name: 'AudienceEditor',
  components: { AudienceFilterRow, ModeCards, SmeAlert },
  props: {
    scope: { type: Object, default: () => ({}) },
    scopeFields: { type: Array, default: () => [] },
    // Company headcount the form already has; left undefined, the editor fetches it.
    savedTotal: { type: Number, default: undefined },
  },
  emits: ['update:scope'],
  setup(props, { emit }) {
    const draft = ref(draftFromScope(props.scope, props.scopeFields));
    let lastEmitted = props.scope;

    // The form can load or replace the scope after mount (edit tab, async scope fields); its
    // echo of what we just emitted is ignored so an empty filter row isn't wiped.
    watch(
      () => [props.scope, props.scopeFields],
      () => {
        if (!sameScope(props.scope, lastEmitted) || draft.value.mode === 'unset') {
          draft.value = draftFromScope(props.scope, props.scopeFields);
          lastEmitted = props.scope;
        }
      },
    );

    const staleValuesFor = key => staleEntries(props.scope, props.scopeFields).find(e => e.key === key)?.values || [];

    const fieldsWithValues = computed(() => props.scopeFields.filter(f => f.values?.length));
    const fieldFor = key =>
      props.scopeFields.find(f => f.key === key) || {
        key,
        label: fieldLabel(key, props.scopeFields),
        values: [],
      };
    const usedKeys = computed(() => new Set(draft.value.rows.map(r => r.key)));
    const unusedFields = computed(() => fieldsWithValues.value.filter(f => !usedKeys.value.has(f.key)));

    const hasValues = computed(() => draft.value.rows.some(r => r.values.length));
    const draftScope = computed(() =>
      draft.value.mode === 'unset' ? null : scopeFromDraft(draft.value, props.scopeFields),
    );
    const { count, total, loading } = useAudienceCount(draftScope, {
      savedTotal: props.savedTotal === undefined ? undefined : toRef(props, 'savedTotal'),
    });

    const isZero = computed(
      () => draft.value.mode === 'filters' && hasValues.value && !loading.value && count.value === 0,
    );
    const countText = computed(() =>
      audienceCountText({ mode: draft.value.mode, count: count.value, total: total.value, loading: loading.value }),
    );

    const emitScope = () => {
      if (draft.value.mode === 'unset') {
        return;
      }
      const unchosen = draft.value.mode === 'filters' && !hasValues.value;
      const scope = scopeFromDraft(unchosen ? { mode: 'unset', rows: [] } : draft.value, props.scopeFields);
      if (!sameScope(scope, lastEmitted)) {
        lastEmitted = scope;
        emit('update:scope', scope);
      }
    };

    // A new row opens its values list; division leads because most companies only use divisions.
    const openRowKey = ref(null);
    const addFilter = key => {
      openRowKey.value = key;
      draft.value.rows.push({ key, values: [] });
      nextTick(() => (openRowKey.value = null));
    };
    const setMode = mode => {
      draft.value.mode = mode;
      if (mode === 'filters' && !draft.value.rows.length && unusedFields.value.length) {
        addFilter((unusedFields.value.find(f => f.key === 'division') || unusedFields.value[0]).key);
      }
      emitScope();
    };
    const modeOptions = computed(() => [
      { value: 'everyone', title: 'Everyone in the company', sub: 'All current employees, including new joiners' },
      {
        value: 'filters',
        title: 'Only some employees',
        sub: 'Choose by division, region or other HR fields',
        disabled: !fieldsWithValues.value.length,
      },
    ]);
    const updateRow = (i, row) => {
      draft.value.rows.splice(i, 1, row);
      emitScope();
    };
    const removeRow = i => {
      draft.value.rows.splice(i, 1);
      emitScope();
    };

    return {
      draft,
      openRowKey,
      staleValuesFor,
      fieldFor,
      fieldsWithValues,
      unusedFields,
      count,
      countText,
      modeOptions,
      loading,
      hasValues,
      isZero,
      setMode,
      addFilter,
      updateRow,
      removeRow,
    };
  },
};
</script>

<style scoped lang="scss">
.audience-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.audience-editor__filters {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.audience-editor__and {
  color: var(--palette-color-brand-primary-darken-10);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding-left: 1rem;
  text-transform: uppercase;
}

.audience-editor__hint {
  color: var(--palette-color-default-lighten-30);
  font-size: 0.8125rem;
  margin: 0.25rem 0 0;
}

.audience-editor__add {
  align-self: flex-start;
}

.audience-editor__count {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
}
</style>
